import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import { api } from '../services/api';
import type { AdminOrg } from '../types';
import { fmtDate } from '../utils/format';

const PLAN_COLORS: Record<string, { bg: string; color: string }> = {
  free:       { bg: '#1e293b', color: '#94a3b8' },
  starter:    { bg: '#1e1b4b', color: '#818cf8' },
  team:       { bg: '#2e1065', color: '#c084fc' },
  business:   { bg: '#14532d', color: '#4ade80' },
  enterprise: { bg: '#451a03', color: '#fb923c' },
};

function PlanBadge({ plan }: { plan: string }) {
  const cfg = PLAN_COLORS[plan] ?? PLAN_COLORS.free;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: cfg.bg,
      color: cfg.color,
      textTransform: 'capitalize',
    }}>
      {plan}
    </span>
  );
}

export default function AccountsPage() {
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/orgs')
      .then(r => setOrgs(r.data.orgs))
      .catch(() => setError('Failed to load orgs'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let out = orgs;
    if (planFilter !== 'all') out = out.filter(o => o.plan === planFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(o =>
        o.name.toLowerCase().includes(q) ||
        o.ownerEmail.toLowerCase().includes(q) ||
        o.orgId.toLowerCase().includes(q)
      );
    }
    return out;
  }, [orgs, search, planFilter]);

  const columns: Column<AdminOrg>[] = [
    {
      key: 'name',
      label: 'Org Name',
      sortable: true,
      render: row => <span style={{ fontWeight: 600 }}>{row.name}</span>,
    },
    { key: 'ownerEmail', label: 'Owner', sortable: true },
    {
      key: 'plan',
      label: 'Plan',
      sortable: true,
      render: row => <PlanBadge plan={row.plan} />,
    },
    { key: 'memberCount', label: 'Members', sortable: true },
    {
      key: 'meetingsProcessedThisMonth',
      label: 'Meetings/Month',
      sortable: true,
      render: row => {
        const limit = row.meetingsPerMonth;
        return `${row.meetingsProcessedThisMonth}${limit ? ` / ${limit}` : ''}`;
      },
    },
    {
      key: 'hasStripe',
      label: 'Billing',
      render: row => row.hasStripe
        ? <span style={{ color: 'var(--success)' }}>✓ Stripe</span>
        : <span style={{ color: 'var(--text-dim)' }}>—</span>,
    },
    {
      key: 'firefliesKeyConfigured',
      label: 'Fireflies',
      render: row => row.firefliesKeyConfigured
        ? <span style={{ color: 'var(--success)' }}>✓ Own key</span>
        : <span style={{ color: 'var(--text-dim)' }}>Shared</span>,
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: row => row.createdAt ? fmtDate(row.createdAt) : '—',
    },
    {
      key: '_actions',
      label: '',
      render: row => (
        <button
          onClick={e => { e.stopPropagation(); navigate(`/reprocess?orgId=${row.orgId}`); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            padding: '4px 10px',
            background: 'var(--accent-subtle)',
            color: 'var(--accent)',
            border: '1px solid var(--accent)',
            borderRadius: 4,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <RefreshCw size={11} />
          Reprocess
        </button>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Accounts</h1>

      {error && (
        <div style={{ padding: '10px 14px', background: 'var(--danger-subtle)', border: '1px solid var(--danger)', borderRadius: 6, marginBottom: 16, fontSize: 13, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search orgs, owners…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1,
            maxWidth: 320,
            padding: '8px 12px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            color: 'var(--text)',
            fontSize: 13,
          }}
        />
        <select
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            color: 'var(--text)',
            fontSize: 13,
          }}
        >
          <option value="all">All plans</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="team">Team</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{filtered.length} org{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <DataTable<AdminOrg>
        columns={columns}
        data={filtered}
        loading={loading}
        emptyMessage="No orgs found"
        rowKey={row => row.orgId}
      />
    </div>
  );
}
